import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../services/user.service';
import { SharedService } from '../../services/sharedService';
import { AVATAR_COLORS } from '../shared/user-avatar/user-avatar.component';

/**
 * A person's own profile: display name and avatar colour.
 *
 * The user menu used to open /user/:id -- the ADMIN user-management screen --
 * so looking at your own account meant opening a form that edits roles, status
 * and regional attachments. This edits three fields and can reach nothing else;
 * the server refuses anything but those three and writes the account named by
 * the token rather than one named in the request.
 */
@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.css'],
    standalone: false,
})
export class UserProfileComponent implements OnInit {
    /** Exactly the colours the server will accept. */
    readonly colors = AVATAR_COLORS;

    user: any = null;
    name = '';
    surname = '';
    avatarColor = '';
    loading = true;
    saving = false;

    constructor(
        private userService: UserService,
        private alertService: ToastrService,
        private translate: TranslateService,
        private sharedService: SharedService,
    ) {}

    ngOnInit(): void {
        this.userService.getCurrentUser().subscribe({
            next: (user: any) => {
                this.user = user || {};
                this.name = this.user.name || '';
                this.surname = this.user.surname || '';
                this.avatarColor = this.user.avatarColor || '';
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.alertService.error(this.translate.instant('PROFILE.LOAD_FAILED'));
            },
        });
    }

    /**
     * What the avatar will look like once saved, without waiting for the save.
     * A copy rather than the live user, so abandoning the page does not leave
     * the header showing a colour that was never stored.
     */
    get preview(): any {
        return {
            username: this.user?.username,
            name: this.name,
            surname: this.surname,
            avatarColor: this.avatarColor,
        };
    }

    chooseColor(color: string): void {
        this.avatarColor = this.avatarColor === color ? '' : color;
    }

    /** Empty means "use the colour derived from my username", not "no colour". */
    get usingDerivedColor(): boolean {
        return !this.avatarColor;
    }

    save(): void {
        if (this.saving) return;
        this.saving = true;

        this.userService.updateOwnProfile({
            name: this.name.trim(),
            surname: this.surname.trim(),
            avatarColor: this.avatarColor,
        }).subscribe({
            next: (user: any) => {
                this.saving = false;
                this.user = user || this.user;
                this.alertService.success(this.translate.instant('PROFILE.SAVED'));
                // The header holds its own copy of the user, so it has to be
                // told: otherwise the avatar keeps the old initials and colour
                // until the next full page load.
                this.sharedService.updateToken.emit(true);
            },
            error: (err: any) => {
                this.saving = false;
                const code = (err && err.error && err.error.error) || 'PROFILE_NOT_SAVED';
                this.alertService.error(this.translate.instant('PROFILE.ERROR.' + code));
            },
        });
    }
}
